import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, Users, Filter, Star, Award, MapPin, Code, Building2,
  ArrowLeft, ChevronRight, BookmarkPlus, Bookmark, ExternalLink,
  Loader, X, Mail, Trophy, GraduationCap, Briefcase, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VerifiedBadge from '../components/VerifiedBadge';
import useVerifiedBadges from '../hooks/useVerifiedBadges';

const API = process.env.REACT_APP_BACKEND_URL;

const TalentSearchPage = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [savedCandidates, setSavedCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  
  const [filters, setFilters] = useState({
    search: '',
    skills: [],
    colleges: [],
    min_score: null,
    max_score: null,
    looking_for: [],
    verified_only: false,
    page: 1,
    limit: 20
  });
  
  const [skillInput, setSkillInput] = useState('');
  const candidateBadges = useVerifiedBadges(candidates.map(c => c.id));
  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchFilterOptions();
    fetchSavedCandidates();
  }, [token, navigate]);

  useEffect(() => {
    searchTalent();
  }, [page]);

  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/api/company/talent-search/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilterOptions(res.data);
    } catch (err) {
      console.error('Failed to load filter options');
    }
  };

  const fetchSavedCandidates = async () => {
    try {
      const res = await axios.get(`${API}/api/company/saved-candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedCandidates(res.data.saved_candidates || []);
    } catch (err) {
      console.error('Failed to load saved candidates');
    }
  };

  const searchTalent = async () => {
    setLoading(true);
    try {
      const payload = {
        ...filters,
        skills: filters.skills.length > 0 ? filters.skills : null,
        colleges: filters.colleges.length > 0 ? filters.colleges : null,
        looking_for: filters.looking_for.length > 0 ? filters.looking_for : null,
        search: filters.search || null,
        page
      };
      
      const res = await axios.post(`${API}/api/company/talent-search`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCandidates(res.data.candidates || []);
      setTotalCandidates(res.data.total || 0);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    searchTalent();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      skills: [],
      colleges: [],
      min_score: null,
      max_score: null,
      looking_for: [],
      verified_only: false,
      page: 1,
      limit: 20
    });
    setPage(1);
  };

  const toggleSkill = (skill) => {
    setFilters(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const addCustomSkill = () => {
    if (skillInput.trim() && !filters.skills.includes(skillInput.trim())) {
      setFilters(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const toggleLookingFor = (option) => {
    setFilters(prev => ({
      ...prev,
      looking_for: prev.looking_for.includes(option)
        ? prev.looking_for.filter(o => o !== option)
        : [...prev.looking_for, option]
    }));
  };

  const saveCandidate = async (studentId) => {
    try {
      await axios.post(
        `${API}/api/company/saved-candidates`,
        { student_id: studentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Candidate saved');
      fetchSavedCandidates();
    } catch (err) {
      toast.error('Failed to save candidate');
    }
  };

  const removeSavedCandidate = async (studentId) => {
    try {
      await axios.delete(`${API}/api/company/saved-candidates/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed from saved');
      fetchSavedCandidates();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const isSaved = (studentId) => savedCandidates.some(s => s.student_id === studentId);

  const lookingForLabels = {
    internship: 'Internship',
    full_time: 'Full-time',
    part_time: 'Part-time',
    freelance: 'Freelance',
    open_to_all: 'Open to All'
  };

  const activeFilterCount = filters.skills.length + filters.colleges.length + 
    filters.looking_for.length + (filters.min_score ? 1 : 0) + (filters.max_score ? 1 : 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/company/dashboard" className="text-gray-400 hover:text-gray-600">
                <ArrowLeft size={20} />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Search className="text-white" size={20} />
                </div>
                <div>
                  <h1 className="font-bold text-gray-900">Talent Search</h1>
                  <p className="text-xs text-gray-500">Find top developers on EvalentX Arena</p>
                </div>
              </div>
            </div>
            
            <Link
              to="/company/saved-candidates"
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Bookmark size={18} />
              Saved ({savedCandidates.length})
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, college, or bio..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                data-testid="talent-search-input"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
                showFilters || activeFilterCount > 0
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter size={18} />
              Filters
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 bg-white/20 rounded-full text-xs flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-cyan-500 text-white rounded-xl font-medium hover:bg-cyan-600 transition-colors"
              data-testid="search-btn"
            >
              Search
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && filterOptions && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
              {/* Skills */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Skills</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {filterOptions.skills?.slice(0, 20).map(skill => (
                    <button
                      key={skill}
                      onClick={() => toggleSkill(skill)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        filters.skills.includes(skill)
                          ? 'bg-cyan-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomSkill())}
                    placeholder="Add custom skill..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    onClick={addCustomSkill}
                    className="px-4 py-2 bg-cyan-100 text-cyan-700 rounded-lg text-sm hover:bg-cyan-200"
                  >
                    Add
                  </button>
                </div>
                {filters.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {filters.skills.map(skill => (
                      <span key={skill} className="flex items-center gap-1 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm">
                        {skill}
                        <button onClick={() => toggleSkill(skill)} className="hover:text-cyan-900">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Score Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Min Score</label>
                  <input
                    type="number"
                    value={filters.min_score || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, min_score: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder={`Min: ${filterOptions.score_range?.min || 0}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Score</label>
                  <input
                    type="number"
                    value={filters.max_score || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, max_score: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder={`Max: ${filterOptions.score_range?.max || 1000}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>

              {/* Looking For */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Looking For</label>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.looking_for_options?.map(option => (
                    <button
                      key={option}
                      onClick={() => toggleLookingFor(option)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        filters.looking_for.includes(option)
                          ? 'bg-cyan-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {lookingForLabels[option] || option}
                    </button>
                  ))}
                </div>
              </div>

              {/* EvalentX Verified Talent filter */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-amber-50 border border-purple-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!filters.verified_only}
                    onChange={(e) => setFilters(prev => ({ ...prev, verified_only: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 accent-purple-600"
                    data-testid="verified-only-filter"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} className="text-purple-600" />
                      <span className="font-semibold text-gray-900 text-sm">Verified Talent only</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-600 text-white rounded font-bold uppercase tracking-wide">New</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">Show only EvalentX AI-verified candidates with active badges.</p>
                  </div>
                </label>
              </div>

              {/* Clear Filters */}
              <div className="flex justify-between pt-4 border-t border-gray-100">
                <button
                  onClick={clearFilters}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Clear all filters
                </button>
                <button
                  onClick={() => { handleSearch(); setShowFilters(false); }}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm hover:bg-cyan-600"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-600">
            {totalCandidates > 0 ? `${totalCandidates} developers found` : 'Search for talent'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-cyan-500" size={32} />
          </div>
        ) : candidates.length > 0 ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Candidates List */}
            <div className="lg:col-span-2 space-y-4">
              {candidates.map(candidate => (
                <div
                  key={candidate.id}
                  onClick={() => setSelectedCandidate(candidate)}
                  className={`bg-white rounded-xl border p-5 cursor-pointer transition-all hover:shadow-md ${
                    selectedCandidate?.id === candidate.id 
                      ? 'border-cyan-400 ring-2 ring-cyan-100' 
                      : 'border-gray-200'
                  }`}
                  data-testid={`candidate-card-${candidate.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {(candidate.full_name || 'U').charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{candidate.full_name || 'Unknown'}</h3>
                          <VerifiedBadge
                            isVerified={!!candidateBadges[candidate.id]}
                            planCode={candidateBadges[candidate.id]?.plan_code}
                            size="sm"
                          />
                        </div>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <GraduationCap size={14} />
                          {candidate.college || 'Not specified'}
                        </p>
                        {candidate.profile?.looking_for && (
                          <p className="text-sm text-cyan-600 mt-1">
                            Looking for: {lookingForLabels[candidate.profile.looking_for] || candidate.profile.looking_for}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          isSaved(candidate.id) 
                            ? removeSavedCandidate(candidate.id)
                            : saveCandidate(candidate.id);
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                          isSaved(candidate.id)
                            ? 'text-cyan-600 bg-cyan-50 hover:bg-cyan-100'
                            : 'text-gray-400 hover:text-cyan-600 hover:bg-cyan-50'
                        }`}
                        title={isSaved(candidate.id) ? 'Remove from saved' : 'Save candidate'}
                      >
                        {isSaved(candidate.id) ? <Bookmark size={20} /> : <BookmarkPlus size={20} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 mt-3 text-sm">
                    {candidate.total_score > 0 && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <Award size={14} />
                        Score: {candidate.total_score}
                      </span>
                    )}
                    {candidate.rank && (
                      <span className="flex items-center gap-1 text-purple-600">
                        <Trophy size={14} />
                        Rank #{candidate.rank}
                      </span>
                    )}
                    {candidate.problems_solved > 0 && (
                      <span className="flex items-center gap-1 text-green-600">
                        <Code size={14} />
                        {candidate.problems_solved} problems solved
                      </span>
                    )}
                  </div>
                  
                  {candidate.profile?.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {candidate.profile.skills.slice(0, 5).map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                          {skill}
                        </span>
                      ))}
                      {candidate.profile.skills.length > 5 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                          +{candidate.profile.skills.length - 5} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Pagination */}
              {totalCandidates > 20 && (
                <div className="flex justify-center gap-2 pt-4">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-gray-600">
                    Page {page} of {Math.ceil(totalCandidates / 20)}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= Math.ceil(totalCandidates / 20)}
                    className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            {/* Selected Candidate Detail */}
            <div className="space-y-4">
              {selectedCandidate ? (
                <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                      {(selectedCandidate.full_name || 'U').charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{selectedCandidate.full_name}</h3>
                      <p className="text-sm text-gray-500">{selectedCandidate.college}</p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    {selectedCandidate.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="text-gray-400" size={16} />
                        <a href={`mailto:${selectedCandidate.email}`} className="text-cyan-600 hover:underline">
                          {selectedCandidate.email}
                        </a>
                      </div>
                    )}
                    {selectedCandidate.total_score > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <Award className="text-amber-500" size={16} />
                        <span>EvalentX Arena Score: <strong>{selectedCandidate.total_score}</strong></span>
                      </div>
                    )}
                    {selectedCandidate.rank && (
                      <div className="flex items-center gap-2 text-sm">
                        <Trophy className="text-purple-500" size={16} />
                        <span>National Rank: <strong>#{selectedCandidate.rank}</strong></span>
                      </div>
                    )}
                  </div>

                  {selectedCandidate.profile?.bio && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">About</h4>
                      <p className="text-sm text-gray-600">{selectedCandidate.profile.bio}</p>
                    </div>
                  )}

                  {selectedCandidate.profile?.skills?.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCandidate.profile.skills.map((skill, idx) => (
                          <span key={idx} className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-lg text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {selectedCandidate.username && (
                      <Link
                        to={`/codearena/profile/${selectedCandidate.username}`}
                        target="_blank"
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                      >
                        <ExternalLink size={18} />
                        View Full Profile
                      </Link>
                    )}
                    <button
                      onClick={() => isSaved(selectedCandidate.id) 
                        ? removeSavedCandidate(selectedCandidate.id)
                        : saveCandidate(selectedCandidate.id)
                      }
                      className={`flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg transition-colors ${
                        isSaved(selectedCandidate.id)
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
                      }`}
                    >
                      {isSaved(selectedCandidate.id) ? (
                        <>
                          <Bookmark size={18} />
                          Saved
                        </>
                      ) : (
                        <>
                          <BookmarkPlus size={18} />
                          Save Candidate
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="text-center text-gray-500">
                    <Users className="mx-auto mb-3 text-gray-300" size={40} />
                    <p>Select a candidate to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Search className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Find Your Next Hire</h3>
            <p className="text-gray-500 mb-4">
              Search EvalentX Arena's developer pool by skills, score, or location
            </p>
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600"
            >
              Start Searching
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default TalentSearchPage;
